import React, { useEffect, useContext, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import styles from './SuppliersProfile.module.css';
import BackBtn from '../../../assets/BackBtn.svg';
import Dropdown from '../../../Components/DropDownBox/Dropdown';
import AdminContext from '../../../Context/adminContext';
import { useMsal } from "@azure/msal-react";
import EditIcon from '../../../assets/Edit.svg';
import { Box, CircularProgress, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportButtonSx } from '../../../GlobalStyles/buttonStyles';
import SubcontractorTaskTable from '../../../Sections/supplier/subcontractorTaskTable';
import SearchBar from '../../../Components/SearchBar/SearchBar';
import { useSelector, useDispatch } from "../../../redux/store";
import {
  fetchRunnerOptions,
  getSubcontractorProjects,
  exportSubcontractorProjects,
  exportReset,
  setSubcontractorProjectsLoading
} from "../../../redux/slices/suppliers";
import { setShowSuccess } from "../../../redux/slices/project";
import EditDiv from '../../../Components/EditDiv/EditDiv';
import { fetchSupplierProfileInfo, updateSupplierInformation } from '../../../redux/slices/suppliersDashboard';

function SuppliersProfile() {
  const location = useLocation();

  const supplierID = useMemo(() => {
    // Prefer hash (current routing convention: /suppliers/SupplierProfile#<id>)
    const hashRaw = (location.hash || '').toString().trim().replace(/^#/, '');
    const fromHash = Number.parseInt(hashRaw, 10);
    if (Number.isFinite(fromHash)) return fromHash;

    // Fallback: allow query string usage (/suppliers/SupplierProfile?supplierID=<id>)
    const qs = new URLSearchParams(location.search || '');
    const qsRaw = qs.get('supplierID') || qs.get('supplierId') || qs.get('id');
    const fromQs = Number.parseInt((qsRaw || '').toString(), 10);
    if (Number.isFinite(fromQs)) return fromQs;

    return null;
  }, [location.hash, location.search]);

  const { enqueueSnackbar } = useSnackbar();
  const { accounts } = useMsal();
  const account = accounts[0];
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const statusOptions = ['TBA', 'S3 Prep', 'S3 End', 'S4 Prep', 'S4 End', 'Finish'];

  const {
    runnerOptions,
    subcontractorProjects,
    isSubcontractorProjectsLoading,
    exportStatus,
    exportError
  } = useSelector((state) => state.suppliers);

  const { supplierProfile, supplierProfileLoading, supplierProfileError } = useSelector((state) => state.suppliersDashboard);

  const { showSuccess } = useSelector((state) => state.project);

  const [loading, setLoading] = useState(false);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [runnerFilter, setRunnerFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState([]);

  const [currentData, setCurrentData] = useState(null);
  const [displayEditDiv, setDisplayEditDiv] = useState(false);
  const [supplierEditInfo, setSupplierEdit] = useState([]);
  const [editType, setEditType] = useState('UPDATE');
  const editable = useContext(AdminContext);
  const [leftBoxEditData, setLeftBoxEditData] = useState(null);
  const [rightBoxEditData, setRightBoxEditData] = useState(null);
  const profilePromiseRef = useRef(null);

  // Fetch data
  const fetchDataRecords = async () => {
    try {
      if (!supplierID) return;
      const data = await dispatch(fetchSupplierProfileInfo({ supplierId: supplierID })).unwrap();
      setSupplierEdit(data?.UpdateSupplierInfo);
      // Initialize edit data for left and right boxes
      prepareEditData(data);
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };

  // Prepare edit data for the info boxes
  const prepareEditData = (data) => {
    if (data && data.type === 'Subcontractor') {
      // Prepare left box edit data
      setLeftBoxEditData({
        DataEditTitle: "Contact & Operational Info",
        DataEdit: [
          {
            dataName: "Key Contact",
            editType: "TextBox",
            data: data.name || ""
          },
          {
            dataName: "Email",
            editType: "TextBox",
            data: data.email || ""
          },
          {
            dataName: "Hours of work",
            editType: "TextBox",
            data: data.working_hours || ""
          },
          {
            dataName: "Hourly Rate",
            editType: "TextBox",
            data: data.hourly_rate ? data.hourly_rate.toString() : ""
          }
        ]
      });

      // Prepare right box edit data
      setRightBoxEditData({
        DataEditTitle: "Financial & Administrative Info",
        DataEdit: [
          {
            dataName: "Payment Terms",
            editType: "TextBox",
            data: data.payment_terms || ""
          },
          {
            dataName: "Cost for services",
            editType: "TextBox",
            data: data.cost_for_services || ""
          },
          {
            dataName: "Internal Level",
            editType: "TextBox",
            data: data.internal_level || ""
          }
        ]
      });
    }
  };

  useEffect(() => {
    if (!supplierID) return;
    dispatch(fetchRunnerOptions());
    dispatch(getSubcontractorProjects(supplierID));
    profilePromiseRef.current?.abort?.();
    profilePromiseRef.current = dispatch(fetchSupplierProfileInfo({ supplierId: supplierID }));
    profilePromiseRef.current
      ?.unwrap?.()
      .then((data) => {
        setSupplierEdit(data?.UpdateSupplierInfo);
        prepareEditData(data);
      })
      .catch((e) => {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        console.error('Error fetching supplier data:', e);
      });

    return () => profilePromiseRef.current?.abort?.();
  }, [dispatch, supplierID]);

  useEffect(() => {
    if (showSuccess) {
      enqueueSnackbar('Successfully Added!', { variant: "success" });
      dispatch(setShowSuccess(false));
    }
  }, [showSuccess, dispatch]);

  useEffect(() => {
    if (exportStatus === 'succeeded') {
      enqueueSnackbar('Export successful!', { variant: 'success' });
      dispatch(exportReset());
    } else if (exportStatus === 'failed') {
      enqueueSnackbar(exportError || 'Failed to export projects', { variant: 'error' });
      dispatch(exportReset());
    }
  }, [exportStatus, exportError, enqueueSnackbar, dispatch]);

  const handleUpdateClick = async (DataTitle, selectedValues) => {
    setLoading(true);

    try {
      const data = await dispatch(updateSupplierInformation({
        actionType: editType,
        sectionName: DataTitle,
        updatedValues: selectedValues,
        email: account.username,
        rowId: supplierID,
      })).unwrap();
      setApiResponse(data?.message || 'Update completed.');
    } catch (error) {
      console.error("Error in supplier Info Update:", error);
      setApiResponse('Error updating supplier (please try again).');
    } finally {
      setIsResponseShown(true);
      setLoading(false);
      fetchDataRecords();
    }
  };

  const handleEdit = (data) => () => {
    setEditType('UPDATE')
    setCurrentData(data);
    setDisplayEditDiv(true);
  };

  const handleLeftBoxEdit = () => {
    setEditType('UPDATE');
    setCurrentData(leftBoxEditData);
    setDisplayEditDiv(true);
  };

  const handleRightBoxEdit = () => {
    setEditType('UPDATE');
    setCurrentData(rightBoxEditData);
    setDisplayEditDiv(true);
  };

  const handleSearch = (event) => {
    setInputValue(event.target.value);
  };

  // Handle runner filter
  const handleRunnerFilter = (runner) => {
    setRunnerFilter(runner);
  };

  // Handle status filter
  const handleStatusFilter = (statuses) => {
    setStatusFilter(statuses);
  };

  // Filter subcontractorProjects based on search term, runner, and status
  const filteredData = Array.isArray(subcontractorProjects)
    ? subcontractorProjects.filter((row) => {
      const searchLower = inputValue.toLowerCase();

      const matchesSearch =
        row.projectAddress.toLowerCase().includes(searchLower) ||
        row.runner.toLowerCase().includes(searchLower) ||
        row.taskOutsourced.toLowerCase().includes(searchLower);

      const matchesRunner = runnerFilter === 'All' || row.runner === runnerFilter;
      const matchesStatus = statusFilter.length === 0 || 
                            statusFilter.includes(row.status) ||
                            (statusFilter.includes('Finish') && row.status.toLowerCase().includes('finish'));

      return matchesSearch && matchesRunner && matchesStatus;
    })
    : [];

  const handleExportClick = () => {
    dispatch(exportSubcontractorProjects(supplierID));
  };

  return (
    <div className={styles.SuppliersProfileContent}>
      <div className={styles.SupplierBaseInfo}>
        <div className={styles.BackBtn} onClick={() => navigate(-1)}>
          <img src={BackBtn} alt='Back' />
        </div>
        <div className={styles.SupplierName}>
          {supplierProfileError
            ? "Failed to load supplier"
            : supplierProfileLoading
              ? "Loading..."
              : supplierProfile
                ? `${supplierProfile.company}`
                : "--"}
          {editable && supplierProfile &&
            <img
              className={`${styles.editIcon} ${styles.Name}`}
              onClick={handleEdit(supplierEditInfo)}
              src={EditIcon}
              alt="EditIconName"
            />
          }
        </div>
        <div className={styles.SupplierServices}>
          {supplierProfile ? `${supplierProfile.services}` : "--"}
        </div>
        <div className={styles.ProjectTags}>
          <li className={styles.PlaceHolderBubble} />

          <div className={styles.LeftAlignment}>
            {/* Status Bubble */}
            {/* {supplierProfile && (
              <React.Fragment>
                {supplierProfile.status && (
                  <li className={styles.StatusBubble}>{supplierProfile.status}</li>
                )}
              </React.Fragment>
            )} */}
          </div>
        </div>
      </div>

      <div className={styles.SuppliersProfileScrollableArea}>
        <div className={styles.ProjectInformation} style={{ display: 'flex', justifyContent: 'space-between' }}>

          <div className={styles.ProjectDetails} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: '1' }}>

            <div className={styles.ProjectStageInfo}>
              {editable && supplierProfile && supplierProfile.type === 'Subcontractor' &&
                <img
                  className={`${styles.editIcon} ${styles.SupplierDetails}`}
                  onClick={handleLeftBoxEdit}
                  src={EditIcon}
                  alt="EditLeftBox"
                  style={{ top: '15px', right: '6px' }}
                />
              }
              <div className={styles.HorBlock1}>
                {supplierProfile && supplierProfile.type === 'Subcontractor' ?
                  <React.Fragment>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.Name} ${styles.FieldTitle}`} style={{ marginLeft: '28px' }}>Key Contact:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.name ? supplierProfile.name : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.Email} ${styles.FieldTitle}`} style={{ marginLeft: '60px' }}>Email:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.email ? supplierProfile.email : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.HoursOfWork} ${styles.FieldTitle}`} style={{ marginLeft: '12px' }}>Hours of work:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.working_hours ? supplierProfile.working_hours : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.HourlyRate} ${styles.FieldTitle}`}>Hourly Wage:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.hourly_rate ? `£${supplierProfile.hourly_rate} p/h` : '--'}</span>
                    </div>
                  </React.Fragment>
                  :
                  <React.Fragment>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.PhoneNo} ${styles.FieldTitle}`}>Phone No:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.phone ? supplierProfile.phone : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.Location} ${styles.FieldTitle}`} style={{ marginLeft: '10.5px' }}>Website:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.website ? supplierProfile.website : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.Email} ${styles.FieldTitle}`} style={{ marginLeft: '25px' }}>Email:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.email ? supplierProfile.email : '--'}</span>
                    </div>
                  </React.Fragment>
                }
              </div>
            </div>

            <div className={styles.ProjectStats}>
              {editable && supplierProfile && supplierProfile.type === 'Subcontractor' &&
                <img
                  className={`${styles.editIcon} ${styles.SupplierDetails}`}
                  onClick={handleRightBoxEdit}
                  src={EditIcon}
                  alt="EditRightBox"
                  style={{ top: '15px', right: '6px' }}
                />
              }
              <div className={styles.HorBlock1} style={{ width: '46%' }}>
                {supplierProfile && supplierProfile.type === 'Subcontractor' ?
                  <React.Fragment>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.PaymentTerms} ${styles.FieldTitle}`} >Payment Terms: </div>
                      <span className={styles.InfoText}>
                        {supplierProfile && supplierProfile.payment_terms ? supplierProfile.payment_terms : '--'}
                      </span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.CostServices} ${styles.FieldTitle}`}>Cost for services:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.cost_for_services ? supplierProfile.cost_for_services : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.InternalLevel} ${styles.FieldTitle}`}>Internal Level:</div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.internal_level ? supplierProfile.internal_level : '--'}</span>
                    </div>
                  </React.Fragment>
                  :
                  <React.Fragment>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.JobFee} ${styles.FieldTitle}`}> Main Contact: </div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.name ? supplierProfile.name : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.JobType} ${styles.FieldTitle}`} style={{ marginLeft: '23px' }}>Comment: </div>
                      <span className={styles.InfoText}>{supplierProfile && supplierProfile.comment ? supplierProfile.comment : '--'}</span>
                    </div>
                    <div className={styles.FieldContainer}>
                      <div className={`${styles.JobType} ${styles.FieldTitle}`} style={{ marginLeft: '25px' }}>Preferred: </div>
                      <span className={styles.InfoText}>
                        {supplierProfile && supplierProfile.preferred ? supplierProfile.preferred : '--'}
                      </span>
                    </div>
                  </React.Fragment>}
              </div>
            </div>
          </div>
        </div>
        {supplierProfile?.type === "Subcontractor" ? (
          isSubcontractorProjectsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', width: '100%' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <React.Fragment>
              <div className={styles.SubTitle}>Project List</div>
              <div style={{ marginBottom: '24px' }}>
                <div className='ProjectTableFilters'>
                  <div className='StatusNdTeam'>
                    <div className='ProjectTeamFilter'>
                      <label className='FilterLabel' htmlFor='RunnerLabel' style={{ fontSize: '12px' }}>Runner:</label>
                      <Dropdown
                        id='RunnerLabel'
                        options={runnerOptions}
                        initialSelectedValue={'All'}
                        onValueChange={handleRunnerFilter}
                        disabled={isSubcontractorProjectsLoading}
                        PopUp={false}
                      />
                    </div>

                    <div className='ProjectStatusFilter'>
                      <label className='FilterLabel' htmlFor='StatusLabel' style={{ fontSize: '12px' }}>Status:</label>
                      <Dropdown
                        id='StatusLabel'
                        options={statusOptions}
                        initialSelectedValue={statusFilter}
                        onValueChange={handleStatusFilter}
                        disabled={isSubcontractorProjectsLoading}
                        PopUp={false}
                        mode='multi'
                      />

                    </div>
                  </div>
                  <div className='ProjectListSearch' style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <SearchBar
                      value={inputValue}
                      onChange={handleSearch}
                      disabled={isSubcontractorProjectsLoading}
                    />
                    <Button
                      variant="contained"
                      onClick={handleExportClick}
                      disabled={isSubcontractorProjectsLoading || exportStatus === 'loading'}
                      startIcon={<FileDownloadIcon sx={{ fontSize: '16px' }}/>}
                      sx={exportButtonSx}
                    >
                      {exportStatus === 'loading' ? 'Exporting...' : 'Export'}
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ overflowX: 'auto', maxWidth: '80vw', margin: '0 auto' }}>
                <SubcontractorTaskTable
                  subcontractorId={supplierID}
                  subcontractorName={supplierProfile?.company}
                  rowData={filteredData}
                  isAdmin={editable}
                  isLoading={isSubcontractorProjectsLoading}
                />
              </div>


            </React.Fragment>
          )
        ) : null}

        {displayEditDiv && <EditDiv
          data={currentData}
          handleUpdateClick={handleUpdateClick}
          setDisplayEditDiv={setDisplayEditDiv}
          actionType={editType}
        />}

        {isResponseShown && (
          <div className={styles.ProjectProfileOverlay}>
            <div className={styles.ProjectProfileResponseBox}>
              <div
                style={{
                  fontSize: 16,
                  fontFamily: "PT Sans",
                  textAlign: "center",
                  margin: "20px",
                  marginLeft: "25px",
                  marginRight: "25px",
                }}
                dangerouslySetInnerHTML={{ __html: apiResponse }}
              />

              <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                  className={styles.ProjectProfileButton}
                  onClick={() => setIsResponseShown(false)}
                >
                  OK
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuppliersProfile;